import { useState, useEffect, useCallback, useRef } from 'react';
import { Conversation, Message, ChatAPIService, ChatAuthService } from '../../services/chatService';
import { DISABLE_BACKEND_CHATBOT_CALLS } from '../../services/chatConfig';
import { useAuth } from '../../context/AuthContextZustand';
import { useToast } from '../../components/UI';
import { useChatFetch } from './useChatFetch';
import { useChatMutations } from './useChatMutations';
import { useChatWebSocket } from './useChatWebSocket';
import {
  extractMessageData,
  createMessage,
  messageExists,
  updateConversationStateInList,
  updateConversationLastMessage,
  countWaitingConversations,
} from './helpers';

/* eslint-disable max-lines-per-function */
/**
 * Hook principal para gerenciar chat.
 * Só chama rotas do chat (conversations, waiting-count) e WebSocket após o auth do chat ter sucesso,
 * evitando requisições duplicadas e falhas após o login principal.
 */
export const useChat = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [chatAuthReady, setChatAuthReady] = useState(false);
  const [waitingCount, setWaitingCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const selectedConversationRef = useRef<Conversation | null>(null);
  const hasShownChatUnavailableRef = useRef(false);

  const {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    loading,
    error,
    setError,
    loadConversations,
    loadConversation,
    loadMessages,
  } = useChatFetch();

  const loadWaitingCount = useCallback(async () => {
    if (!user) return;
    try {
      const count = await ChatAPIService.getWaitingCount();
      setWaitingCount(count);
    } catch (err) {
      const count = countWaitingConversations(conversations);
      setWaitingCount(count);
    }
  }, [conversations, user]);

  // Ref para conversa selecionada
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Handler de nova mensagem
  const handleNewMessage = useCallback(
    (data: unknown) => {
      const messageData = extractMessageData(data);
      if (!messageData) return;

      const newMessage = createMessage(messageData);
      if (!newMessage) return;

      const conversationId = messageData.conversationId;

      // Atualizar mensagens se conversa está selecionada
      if (conversationId && conversationId === selectedConversationRef.current?.id) {
        setMessages((prev) => {
          if (messageExists(prev, newMessage.id)) return prev;
          return [...prev, newMessage];
        });

        if (newMessage.sender === 'CLIENT') {
          ChatAPIService.markAsRead(conversationId).catch((error) => {
            // Ignora erro silenciosamente - marcar como lido é uma ação não-crítica
            console.debug('Erro ao marcar mensagem como lida:', error);
          });
        }
      }

      // Atualizar lista de conversas
      setConversations((prev) =>
        updateConversationLastMessage(
          prev,
          conversationId || '',
          newMessage,
          selectedConversationRef.current?.id || null
        )
      );
    },
    [setMessages, setConversations]
  );

  // Handler de mudança de estado
  const handleConversationStateChange = useCallback(
    (conversationId: string, newState: string) => {
      setConversations((prev) => updateConversationStateInList(prev, conversationId, newState));

      if (selectedConversation?.id === conversationId) {
        setSelectedConversation((prev) =>
          prev ? { ...prev, state: newState as Conversation['state'] } : null
        );
      }
    },
    [selectedConversation, setConversations, setSelectedConversation]
  );

  // Handler de typing
  const handleTyping = useCallback((conversationId: string) => {
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 3000);
  }, []);

  // Handler de waiting for human
  const handleWaitingForHuman = useCallback(() => {
    loadConversations();
    setTimeout(() => loadWaitingCount(), 500);
  }, [loadConversations, loadWaitingCount]);

  // Handler de conversa assumida
  const handleConversationAssumed = useCallback(() => {
    loadConversations();
  }, [loadConversations]);

  // Success callback para mutations
  const handleMutationSuccess = useCallback(async () => {
    await loadConversation(selectedConversation?.id || '');
    await loadConversations();
  }, [loadConversation, loadConversations, selectedConversation?.id]);

  // Mutations
  const {
    assumeConversation,
    returnToBot,
    sendMessage: sendMessageMutation,
  } = useChatMutations(user?.id, user?.name, handleMutationSuccess);

  // Auth do chat: só quando o estabelecimento tem WhatsApp ativo e número aceito (e usuário pode usar chat).
  // Se DISABLE_BACKEND_CHATBOT_CALLS estiver true, não chama o backend-chatbot nem mostra fluxo de chat.
  const canUseChat =
    !DISABLE_BACKEND_CHATBOT_CALLS &&
    user?.permissions?.some((p) => ['iam.manage', 'appointments.read'].includes(p)) &&
    user?.establishmentWhatsAppEnabled === true;

  useEffect(() => {
    if (!user?.id) {
      setChatAuthReady(false);
      ChatAuthService.clearAuth();
      return;
    }
    if (!canUseChat) {
      setChatAuthReady(false);
      ChatAuthService.clearAuth();
      return;
    }
    hasShownChatUnavailableRef.current = false;
    const timer = setTimeout(async () => {
      const ok = await ChatAuthService.autoLogin();
      setChatAuthReady(ok);
      if (!ok && !hasShownChatUnavailableRef.current) {
        hasShownChatUnavailableRef.current = true;
        addToast({
          type: 'warning',
          title: 'WhatsApp temporariamente indisponível',
          message:
            'O atendimento pelo WhatsApp está fora do ar. Você pode continuar usando o painel normalmente.',
        });
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [user?.id, user?.permissions, user?.establishmentWhatsAppEnabled, canUseChat, addToast]);

  // WebSocket connection (só quando chat auth estiver ok)
  useChatWebSocket({
    userId: user?.id,
    chatAuthReady,
    onNewMessage: handleNewMessage,
    onWaitingCountUpdate: setWaitingCount,
    onConversationStateChange: handleConversationStateChange,
    onWaitingForHuman: handleWaitingForHuman,
    onConversationAssumed: handleConversationAssumed,
    onTyping: handleTyping,
    selectedConversationId: selectedConversation?.id || null,
  });

  // Enviar mensagem wrapper
  const sendMessage = useCallback(
    async (conversationId: string, message: string) => {
      const tempMessage = await sendMessageMutation(conversationId, message);
      setMessages((prev) => [...prev, tempMessage]);
    },
    [sendMessageMutation, setMessages]
  );

  // Load waiting count e conversations só após auth do chat ter sucesso
  useEffect(() => {
    if (!user || !chatAuthReady) return;
    const t1 = setTimeout(() => loadWaitingCount(), 300);
    const t2 = setTimeout(() => loadConversations(), 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [user, chatAuthReady, loadWaitingCount, loadConversations]);

  return {
    conversations,
    selectedConversation,
    messages,
    waitingCount,
    loading,
    error,
    isTyping,
    loadConversations,
    loadConversation,
    loadMessages,
    loadWaitingCount,
    assumeConversation,
    returnToBot,
    sendMessage,
    setSelectedConversation,
  };
};
